// ===========================================
// Needs Checking Card (#642 Phase 2 Request 7, admin dashboard)
// ===========================================
// Every payment the owner flagged as needing checking against the bank, oldest first, until it is
// resolved. The OWNER's ("Principal for now"): shown and queried for the owner tier only, and out
// of the way when there is nothing to check.

import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Card, CardContent, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { usePaymentsNeedingCheck } from '../../hooks/useInvoices';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { formatCurrencyFromDollars } from '../../utils/currency';
import { PAYMENT_CHECK_REASON_LABELS, formatPaymentDay, PAYMENT_METHOD_LABELS } from '../../utils/invoiceDisplay';

export default function NeedsCheckingCard() {
  const { isOwner } = usePermissions();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const timeZone = useSchoolTimezone();
  const { data, isLoading, isError } = usePaymentsNeedingCheck(isOwner);

  if (!isOwner) return null;
  if (!isLoading && !isError && (data ?? []).length === 0) return null;

  return (
    <Card data-testid="needs-checking-card">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Payments to check
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Alert severity="error">Could not load the payments to check.</Alert>
        ) : (
          <Stack spacing={1.5}>
            {(data ?? []).map((p) => (
              <Box key={p.id} sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <Link component={RouterLink} to={`/${schoolSlug}/admin/invoices/${p.invoice.id}`}>
                    {p.invoice.invoiceNumber}
                  </Link>
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatCurrencyFromDollars(p.amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {p.familyName} · {PAYMENT_METHOD_LABELS[p.method]} · paid {formatPaymentDay(p.paidAt, timeZone)}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {PAYMENT_CHECK_REASON_LABELS[p.reason]}
                  {p.note ? `: ${p.note}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Flagged {formatPaymentDay(p.setAt, timeZone)}
                  {p.setByName ? ` by ${p.setByName}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
